import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, FileText, Image as ImageIcon, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const ACCEPT = "image/*,.pdf,.csv,.xls,.xlsx,.txt,.doc,.docx";
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const uploadFiles = async (ticketId, files, onProgress) => {
  const out = [];
  for (const f of files) {
    if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: supera 10 MB`); continue; }
    const fd = new FormData(); fd.append("file", f);
    try { out.push((await api.post(`/tickets/${ticketId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (e) => onProgress?.(f.name, e.total ? Math.round((e.loaded / e.total) * 100) : 0) })).data); }
    catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
  }
  return out;
};

export function AttachButton({ ticketId, onUploaded, disabled, testId = "ticket-attach" }) {
  const ref = useRef();
  const [prog, setProg] = useState(null);
  const pick = async (e) => {
    const files = [...e.target.files]; e.target.value = "";
    if (!files.length) return;
    setProg({ name: files[0].name, pct: 0 });
    const added = await uploadFiles(ticketId, files, (name, pct) => setProg({ name, pct }));
    setProg(null);
    if (added.length) { toast.success(`${added.length} allegat${added.length > 1 ? "i caricati" : "o caricato"}`); onUploaded?.(added); }
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" data-testid={`${testId}-input`} onChange={pick} />
      <Button type="button" size="sm" variant="outline" data-testid={`${testId}-button`} disabled={disabled || !!prog} onClick={() => ref.current?.click()}>
        <Paperclip className="mr-1.5 h-3.5 w-3.5" />{prog ? `${prog.name.slice(0, 18)}… ${prog.pct}%` : "Allega file"}
      </Button>
    </>
  );
}

const Thumb = ({ ticketId, a }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u;
    api.get(`/tickets/${ticketId}/attachments/${a.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [ticketId, a.id]);
  return url ? <img src={url} alt={a.name} className="h-20 w-28 rounded-md object-cover" /> : <div className="flex h-20 w-28 items-center justify-center rounded-md bg-slate-100"><ImageIcon className="h-5 w-5 text-slate-400" /></div>;
};

export function AttachmentList({ ticketId, attachments }) {
  if (!attachments?.length) return null;
  const open = async (a) => {
    try {
      const r = await api.get(`/tickets/${ticketId}/attachments/${a.id}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      if (a.content_type.startsWith("image/") || a.content_type === "application/pdf") window.open(u, "_blank");
      else Object.assign(document.createElement("a"), { href: u, download: a.name }).click();
      setTimeout(() => URL.revokeObjectURL(u), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="ticket-attachments">
      {attachments.map((a) => (
        <button type="button" key={a.id} data-testid={`ticket-attachment-${a.id}`} onClick={() => open(a)} className="group flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-1.5 text-left text-xs hover:border-blue-400">
          {a.content_type.startsWith("image/") ? <Thumb ticketId={ticketId} a={a} /> : <div className="flex h-20 w-28 items-center justify-center rounded-md bg-slate-100"><FileText className="h-6 w-6 text-slate-400" /></div>}
          <div className="max-w-[140px] pr-1"><div className="truncate font-medium" title={a.name}>{a.name}</div><div className="text-slate-400">{kb(a.size)} · {a.by.split("@")[0]}</div><div className="mt-1 inline-flex items-center gap-1 text-blue-600 opacity-0 group-hover:opacity-100"><Download className="h-3 w-3" />apri</div></div>
        </button>))}
    </div>
  );
}
